import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp } from 'lucide-react';
import { dashboardApi } from '../services/tenantApi';

const DemandDriverAnalysis = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userContext, setUserContext] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);

  // Generate 90-day date range
  const get90DayRange = () => {
    const today = new Date();
    const start = today.toISOString().split('T')[0];
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 90);
    const end = endDate.toISOString().split('T')[0];
    return { start, end };
  };

  // Fetch demand driver data
  const fetchDemandDriverData = async () => {
    try {
      setLoading(true);
      setError(null);
      setNeedsSetup(false);
      
      // First, get user context to check if hotel is configured
      const context = await dashboardApi.getUserContext();
      setUserContext(context);
      
      // Check if user has hotel configured (check for hotel_id in context)
      if (!context?.hotel_id) {
        console.log('🏨 No hotel configured - showing setup message');
        setNeedsSetup(true);
        setLoading(false);
        return;
      }
      
      // Use hotel_city from dashboard context (no extra fetch needed)
      const location = context.hotel_city?.toLowerCase() || 'bali';
      console.log('🏨 Using hotel for demand drivers:', { hotel_id: context.hotel_id, location });
      
      const { start, end } = get90DayRange();
      console.log('🔍 Fetching 90-day demand driver data:', { start, end, location });
      
      // Use public demand API with hotel's location
      const response = await fetch(`/api/demand?location=${encodeURIComponent(location)}&days=90`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const apiData = await response.json();
      console.log('📊 Demand driver API response structure:', {
        hasItems: !!apiData?.items,
        itemsLength: apiData?.items?.length || 0,
        hasDemandScores: !!apiData?.demand_scores,
        demandScoresLength: apiData?.demand_scores?.length || 0,
        firstItemKeys: apiData?.items?.[0] ? Object.keys(apiData.items[0]) : [],
        sampleItem: apiData?.items?.[0] || apiData?.demand_scores?.[0] || null
      });
      
      // Process the response to extract signal data
      const processedData = processDemandSignals(apiData);
      console.log('📈 Processed demand driver data:', processedData.length, 'days');
      
      setChartData(processedData);
      
    } catch (err) {
      console.error('❌ Error fetching demand driver data:', err);
      setError('Please setup your hotel to see data');
      
      // NO FALLBACK - Leave empty if no API data
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  // Process API response to extract individual signals
  const processDemandSignals = (apiResponse) => {
    // Handle both direct demand_scores array and items array structure
    let demandScores = [];
    
    if (apiResponse?.demand_scores) {
      // Direct demand_scores array (from unified API)
      demandScores = apiResponse.demand_scores;
      console.log('🔍 Processing demand_scores array:', demandScores.length, 'items');
    } else {
      console.log('⚠️ No demand_scores in API response');
      return [];
    }
    
    if (!demandScores || demandScores.length === 0) {
      console.log('⚠️ Empty demand scores');
      return [];
    }
    
    return demandScores.map((item, index) => {
      const date = new Date(item.date);
      const formattedDate = date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
      
      // Extract individual signals (already in 0-100 scale from API)
      const travelSignal = item.travel_signal || 0;
      const accommodationSignal = item.accommodation_signal || 0;
      const interestSignal = item.interest_signal || 0;
      
      // Use the REAL score from unified API (same field as DemandForecastChart)
      const realTotalScore = item.score || 0;
      
      if (index < 5) { // Log first 5 for debugging
        console.log(`📊 Day ${index + 1} (${item.date}):`, {
          travel: travelSignal,
          accommodation: accommodationSignal,
          interest: interestSignal,
          realTotal: realTotalScore,
          apiStructure: Object.keys(item)
        });
      }
      
      return {
        date: item.date,
        formattedDate,
        travel_signal: Number(travelSignal.toFixed(1)),
        accommodation_signal: Number(accommodationSignal.toFixed(1)),
        interest_signal: Number(interestSignal.toFixed(1)),
        total_score: Number(realTotalScore.toFixed(1)) // Use real backend-calculated score
      };
    });
  };

  // Generate mock data for development/fallback
  const generateMockData = () => {
    console.log('🎭 Generating mock demand driver data for 90 days');
    const data = [];
    const today = new Date();
    
    for (let i = 0; i < 90; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      const formattedDate = date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
      
      // Generate realistic seasonal patterns
      const seasonalFactor = 1 + 0.3 * Math.sin((i / 90) * 2 * Math.PI);
      const weeklyFactor = 1 + 0.1 * Math.sin((i / 7) * 2 * Math.PI);
      
      // Base signals with seasonal variation
      const baseTravel = 45 + 15 * seasonalFactor * weeklyFactor;
      const baseAccommodation = 35 + 20 * seasonalFactor;
      const baseInterest = 25 + 10 * weeklyFactor;
      
      // Add some randomness
      const travelSignal = Math.max(10, Math.min(90, baseTravel + (Math.random() - 0.5) * 10));
      const accommodationSignal = Math.max(10, Math.min(90, baseAccommodation + (Math.random() - 0.5) * 15));
      const interestSignal = Math.max(10, Math.min(90, baseInterest + (Math.random() - 0.5) * 8));
      
      // Generate realistic total score (simulating backend calculation with seasonality)
      const baseTotal = travelSignal * 0.45 + accommodationSignal * 0.30 + interestSignal * 0.25;
      const seasonalAdjustment = seasonalFactor * 0.1; // Small seasonal boost/reduction
      const totalScore = Math.max(15, Math.min(95, baseTotal + seasonalAdjustment));
      
      data.push({
        date: dateStr,
        formattedDate,
        travel_signal: Number(travelSignal.toFixed(1)),
        accommodation_signal: Number(accommodationSignal.toFixed(1)),
        interest_signal: Number(interestSignal.toFixed(1)),
        total_score: Number(totalScore.toFixed(1)) // Realistic mock total score
      });
    }
    
    return data;
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 mb-2">{data.formattedDate}</p>
          <div className="space-y-1">
            {payload.reverse().map((entry, index) => (
              <p key={index} className="text-sm flex items-center">
                <span 
                  className="w-3 h-3 rounded-full mr-2" 
                  style={{ backgroundColor: entry.color }}
                ></span>
                <span className="font-medium">{entry.name}:</span>
                <span className="ml-1">{entry.value.toFixed(1)}</span>
              </p>
            ))}
            <div className="border-t pt-1 mt-2">
              <p className="text-sm font-semibold text-gray-900">
                Total Demand Score: {data.total_score}
              </p>
              <p className="text-xs text-gray-500">
                (Backend calculated with seasonality)
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Format X-axis labels (show every 7th day)
  const formatXAxisLabel = (tickItem, index) => {
    if (index % 7 === 0) {
      const data = chartData.find(d => d.date === tickItem);
      return data ? data.formattedDate : tickItem;
    }
    return '';
  };

  // Load data on component mount
  useEffect(() => {
    fetchDemandDriverData();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            90-Day Demand Driver Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-96 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading demand driver analysis...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (needsSetup) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            90-Day Demand Driver Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-96 flex flex-col items-center justify-center text-center">
            <div className="text-6xl mb-4">🏨</div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">Setup Required</h4>
            <p className="text-gray-600 mb-4 max-w-md">
              Configure your hotel details to see demand driver analysis for your location.
            </p>
            <button
              onClick={() => window.location.href = '/app/onboarding'}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Setup Hotel
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          90-Day Demand Driver Analysis
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Breakdown of demand signals: Travel (45%), Accommodation (30%), Interest (25%)
        </p>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">{error} - Showing sample data</p>
          </div>
        )}
        
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                dataKey="date"
                tickFormatter={formatXAxisLabel}
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis 
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                label={{ 
                  value: 'Signal Strength (0-100)', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="rect"
              />
              
              {/* Stacked Areas - Order matters for stacking */}
              <Area
                type="monotone"
                dataKey="travel_signal"
                stackId="1"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.7}
                name="Travel Signal"
              />
              <Area
                type="monotone"
                dataKey="accommodation_signal"
                stackId="1"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.7}
                name="Accommodation Signal"
              />
              <Area
                type="monotone"
                dataKey="interest_signal"
                stackId="1"
                stroke="#8b5cf6"
                fill="#8b5cf6"
                fillOpacity={0.7}
                name="Interest Signal"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Signal Legend with Weights */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <div>
              <span className="font-medium">Travel Signal (45%)</span>
              <p className="text-xs text-muted-foreground">Flight prices & airport activity</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <div>
              <span className="font-medium">Accommodation Signal (30%)</span>
              <p className="text-xs text-muted-foreground">Hotel bookings & events</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-purple-500 rounded"></div>
            <div>
              <span className="font-medium">Interest Signal (25%)</span>
              <p className="text-xs text-muted-foreground">Search volume & trends</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DemandDriverAnalysis;
